import { useState } from 'react';
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { apiRequest } from '../../lib/apiClient.js';
import { queryKeys } from '../../lib/queryKeys.js';
import type {
  AssistantPolicy,
  AssistantPolicyCreateBody,
  AssistantPolicyListResponse,
  AssistantPolicyUpdateBody,
  PolicyFormatRequest,
  PolicyFormatResponse,
} from '../../lib/types.js';
import { formatPolicyDocument } from './policyFormatClient.js';
import {
  appendRule,
  countRuleSections,
  emptyRuleFields,
  isCompleteRule,
  ruleToMarkdown,
  type RuleFields,
} from './ruleMarkdown.js';

const POLICIES_PATH = '/api/assistant-policies';

const invalidatePolicies = (queryClient: QueryClient, tenantId: string): void => {
  void queryClient.invalidateQueries({ queryKey: queryKeys.assistantPolicies(tenantId) });
};

interface PoliciesListLogic {
  isLoading: boolean;
  data: AssistantPolicy[] | undefined;
  error: Error | null;
}

export const usePoliciesListLogic = (tenantId: string | undefined): PoliciesListLogic => {
  const query = useQuery<AssistantPolicyListResponse>({
    queryKey: queryKeys.assistantPolicies(tenantId ?? ''),
    queryFn: () => apiRequest<AssistantPolicyListResponse>(POLICIES_PATH),
    enabled: Boolean(tenantId),
  });

  return {
    isLoading: query.isLoading,
    data: query.data?.policies,
    error: query.error,
  };
};

interface CreatePolicyLogic {
  isSubmitting: boolean;
  error: Error | null;
  create: (body: AssistantPolicyCreateBody) => Promise<AssistantPolicy>;
  reset: () => void;
}

/**
 * Both write paths land here: "Add a rule" into a new document, and saving a
 * formatted upload. The API rejects a duplicate name (409) and an over-budget
 * aggregate (400) — its message is admin-facing, so callers surface `error`
 * verbatim rather than substituting their own copy.
 */
export const useCreatePolicyLogic = (tenantId: string | undefined): CreatePolicyLogic => {
  const queryClient = useQueryClient();

  const mutation = useMutation<AssistantPolicy, Error, AssistantPolicyCreateBody>({
    mutationFn: (body) => apiRequest<AssistantPolicy>(POLICIES_PATH, { method: 'POST', body }),
    onSuccess: () => invalidatePolicies(queryClient, tenantId ?? ''),
  });

  return {
    isSubmitting: mutation.isPending,
    error: mutation.error,
    create: (body) => mutation.mutateAsync(body),
    reset: () => mutation.reset(),
  };
};

interface UpdatePolicyLogic {
  isSubmitting: boolean;
  error: Error | null;
  update: (policyId: string, body: AssistantPolicyUpdateBody) => Promise<AssistantPolicy>;
  reset: () => void;
}

/** Full-document write: appending a rule, or replacing a document from an upload. */
export const useUpdatePolicyLogic = (tenantId: string | undefined): UpdatePolicyLogic => {
  const queryClient = useQueryClient();

  const mutation = useMutation<
    AssistantPolicy,
    Error,
    { policyId: string; body: AssistantPolicyUpdateBody }
  >({
    mutationFn: ({ policyId, body }) =>
      apiRequest<AssistantPolicy>(`${POLICIES_PATH}/${policyId}`, {
        method: 'PUT',
        body,
      }),
    onSuccess: () => invalidatePolicies(queryClient, tenantId ?? ''),
  });

  return {
    isSubmitting: mutation.isPending,
    error: mutation.error,
    update: (policyId, body) => mutation.mutateAsync({ policyId, body }),
    reset: () => mutation.reset(),
  };
};

interface SetPolicyActiveLogic {
  error: Error | null;
  /** The document whose toggle is mid-flight, for a per-card disabled state. */
  pendingId: string | null;
  setActive: (policyId: string, isActive: boolean) => Promise<AssistantPolicy>;
}

/**
 * The active/inactive toggle. Invalidate-refetch rather than optimistic: turning
 * a document on can be refused for overflowing the active budget, and an
 * optimistic switch would show it enforced when it isn't.
 */
export const useSetPolicyActiveLogic = (tenantId: string | undefined): SetPolicyActiveLogic => {
  const queryClient = useQueryClient();

  const mutation = useMutation<AssistantPolicy, Error, { policyId: string; isActive: boolean }>({
    mutationFn: ({ policyId, isActive }) =>
      apiRequest<AssistantPolicy>(`${POLICIES_PATH}/${policyId}`, {
        method: 'PATCH',
        body: { isActive },
      }),
    onSuccess: () => invalidatePolicies(queryClient, tenantId ?? ''),
  });

  return {
    error: mutation.error,
    pendingId: mutation.isPending ? (mutation.variables?.policyId ?? null) : null,
    setActive: (policyId, isActive) => mutation.mutateAsync({ policyId, isActive }),
  };
};

interface DeletePolicyLogic {
  isSubmitting: boolean;
  error: Error | null;
  delete: (policyId: string) => Promise<void>;
}

export const useDeletePolicyLogic = (tenantId: string | undefined): DeletePolicyLogic => {
  const queryClient = useQueryClient();

  const mutation = useMutation<void, Error, string>({
    mutationFn: (policyId) =>
      apiRequest<void>(`${POLICIES_PATH}/${policyId}`, { method: 'DELETE' }),
    onSuccess: () => invalidatePolicies(queryClient, tenantId ?? ''),
  });

  return {
    isSubmitting: mutation.isPending,
    error: mutation.error,
    delete: async (policyId) => {
      await mutation.mutateAsync(policyId);
    },
  };
};

interface FormatPolicyLogic {
  isSubmitting: boolean;
  error: Error | null;
  format: (req: PolicyFormatRequest) => Promise<PolicyFormatResponse>;
  reset: () => void;
}

/** Formats an upload into a constraint catalog. Nothing is cached — the draft is
 *  transient until the admin saves it. */
export const useFormatPolicyLogic = (): FormatPolicyLogic => {
  const mutation = useMutation<PolicyFormatResponse, Error, PolicyFormatRequest>({
    mutationFn: (req) => formatPolicyDocument(req),
  });

  return {
    isSubmitting: mutation.isPending,
    error: mutation.error,
    format: (req) => mutation.mutateAsync(req),
    reset: () => mutation.reset(),
  };
};

/** `targetId` sentinel for "New document…" — no real policy id can collide. */
export const NEW_DOCUMENT = '__new__';

/** One rule saved during this add-rules session, for the running list. */
export interface AddedRule {
  situation: string;
  never: string;
}

interface AddRuleLogic {
  fields: RuleFields;
  setField: (key: keyof RuleFields, value: string) => void;
  /** A policy id, or NEW_DOCUMENT. */
  targetId: string;
  setTargetId: (v: string) => void;
  newName: string;
  setNewName: (v: string) => void;
  /** Rules saved this session, oldest first. */
  added: AddedRule[];
  canSubmit: boolean;
  isSubmitting: boolean;
  error: Error | null;
  submit: () => Promise<AssistantPolicy>;
}

/**
 * The batch rule form. It holds no rules — a rule only ever exists as markdown,
 * so each submit generates a section and either appends it to the chosen
 * document (PUT) or starts a new one with it (POST). A successful save clears
 * the fields for the next rule and records it in `added`; the first save to
 * "New document…" creates it and retargets the session at the created document.
 */
export const useAddRuleLogic = (
  tenantId: string | undefined,
  policies: AssistantPolicy[],
  initialTargetId?: string,
): AddRuleLogic => {
  const createLogic = useCreatePolicyLogic(tenantId);
  const updateLogic = useUpdatePolicyLogic(tenantId);
  const [fields, setFields] = useState<RuleFields>(emptyRuleFields);
  // No selector: an initialTargetId (a document page, or review chaining)
  // appends to that document; otherwise the first save creates a new one.
  const [targetId, setTargetId] = useState<string>(initialTargetId ?? NEW_DOCUMENT);
  const [newName, setNewName] = useState('');
  const [added, setAdded] = useState<AddedRule[]>([]);
  // The server's copy after our last save — appending from it (not the list
  // query's snapshot) means a fast second save can't clobber the first while
  // the invalidated list is still refetching.
  const [lastSaved, setLastSaved] = useState<AssistantPolicy | null>(null);

  const isNew = targetId === NEW_DOCUMENT;
  const target = lastSaved?.id === targetId ? lastSaved : policies.find((p) => p.id === targetId);
  const canSubmit =
    isCompleteRule(fields) &&
    (isNew ? newName.trim().length > 0 : Boolean(target)) &&
    !createLogic.isSubmitting &&
    !updateLogic.isSubmitting;

  const submit = async (): Promise<AssistantPolicy> => {
    const ruleNumber = isNew || !target ? 1 : countRuleSections(target.document) + 1;
    const rule = ruleToMarkdown(fields, `Rule ${String(ruleNumber)}`);
    let saved: AssistantPolicy;
    if (isNew) {
      saved = await createLogic.create({ name: newName.trim(), document: rule });
      // The session now appends to the document it just created.
      setTargetId(saved.id);
      setNewName('');
    } else {
      if (!target) throw new Error('Choose a document to add the rule to.');
      saved = await updateLogic.update(target.id, {
        document: appendRule(target.document, rule),
      });
    }
    setLastSaved(saved);
    setAdded((prev) => [
      ...prev,
      { situation: fields.whenHelpingWith, never: fields.neverInstruct },
    ]);
    setFields(emptyRuleFields);
    return saved;
  };

  return {
    fields,
    setField: (key, value) => setFields((prev) => ({ ...prev, [key]: value })),
    targetId,
    setTargetId,
    newName,
    setNewName,
    added,
    canSubmit,
    isSubmitting: createLogic.isSubmitting || updateLogic.isSubmitting,
    error: createLogic.error ?? updateLogic.error,
    submit,
  };
};

interface SaveDraftLogic {
  name: string;
  setName: (v: string) => void;
  isSubmitting: boolean;
  error: Error | null;
  canSubmit: boolean;
  save: () => Promise<AssistantPolicy>;
}

/**
 * Saving a formatted upload as a new document, named from the filename until
 * the admin edits it. (Replacing an existing document was cut: delete + upload
 * covers it without a second write path.)
 */
export const useSaveDraftLogic = (
  tenantId: string | undefined,
  draft: { document: string; sourceText: string },
  initialName: string,
): SaveDraftLogic => {
  const createLogic = useCreatePolicyLogic(tenantId);
  const [name, setName] = useState(initialName);

  const trimmed = name.trim();

  return {
    name,
    setName,
    isSubmitting: createLogic.isSubmitting,
    error: createLogic.error,
    canSubmit: trimmed.length > 0 && !createLogic.isSubmitting,
    save: () =>
      createLogic.create({
        name: trimmed,
        document: draft.document,
        sourceText: draft.sourceText,
      }),
  };
};
